import { apiFetch } from '@carouselgg/auth/client'
import type { ContactChoice, ContactPersonOption, PrimaryContact } from '@carouselgg/nexus/contact-input'

export interface MyTeam {
  id: string
  name: string
  slug: string
  logo_url: string
  primary_color?: string
  role: string
  joined_at: string
  game_id: string
  game_name: string
  /** Players the game needs to field a side. */
  team_size: number
  /** Ceiling including substitutes, so member_count can exceed team_size. */
  max_roster_size: number
  /** Active roster spots only — a departed player leaves the roster short. */
  member_count: number
  /** Record across finished matches. */
  wins: number
  losses: number
  /** Live competition the team is entered in; empty when it is not. */
  competing_in: string
  organization_id: string
  organization_name: string
  /** A walk-up team with no owner yet that the viewer, a real player on it, can ask to own. */
  owner_requestable?: boolean
  /** The viewer has asked to own it and the league hasn't answered. */
  owner_request_pending?: boolean
  organization_slug: string
  organization_type: string
}

export function getMyTeams(): Promise<{ teams: MyTeam[]; count: number }> {
  return apiFetch<{ teams: MyTeam[]; count: number }>('/teams/mine')
}

export interface OrgSeason {
  id: string
  /** The league hosting it — every org-scoped route is keyed by that org's slug. */
  organization_id: string
  name: string
  slug: string
  status: 'draft' | 'open' | 'active' | 'playoffs' | 'completed' | 'cancelled'
  competition_type: string
  game_name: string
  game_logo_url: string
  division_name: string | null
  starts_at: string | null
  ends_at: string | null
  /**
   * When the registration window opens and shuts. These are the dates a league
   * operator is actually counting down to — a season's own start is downstream
   * of them.
   */
  registration_opens_at: string | null
  registration_closes_at: string | null
  /** Teams entered, and applications waiting on an admin. */
  registered_teams: number
  pending_registrations: number
  /** Fixtures past kickoff with no result reported. */
  overdue_results: number
  max_teams: number | null
}

export function listOrgSeasons(orgID: string): Promise<{ seasons: OrgSeason[] }> {
  return apiFetch<{ seasons: OrgSeason[] }>(`/seasons/${orgID}`)
}

/**
 * A live competition one of your teams is entered in.
 *
 * Answers "what am I in", where /matches/mine answers "when do I play". One row
 * per (competition, team): a player on two teams in the same season appears
 * twice, which is the truth.
 */
export interface MyCompetition {
  season_id: string
  name: string
  slug: string
  status: OrgSeason['status']
  competition_type: string
  organization_id: string
  organization_name: string
  organization_slug: string
  division_name: string
  game_name: string
  game_logo_url?: string
  team_id: string
  team_name: string
  team_slug: string
  /** Season start, for "week N" context. */
  starts_at: string | null
  /** Why it is listed: you are rostered, or an org you administer fields it. */
  reason: 'playing' | 'team'
  registered_teams: number
  /** Slug of the club that owns the team, for its contact endpoints. */
  team_org_slug: string
  registration_id: string
  /** Who the league reaches for this event. Set only when `can_manage_contact`. */
  contact: PrimaryContact | null
  contact_missing?: 'unreachable' | 'minor' | 'none'
  /** The viewer manages this team, so they see and may change its contact. */
  can_manage_contact: boolean
}

export interface TeamContactInfo {
  contact: PrimaryContact | null
  options: ContactPersonOption[]
}

/** The team's people and the methods each verified, to pick an event contact from. */
export function getTeamContactInfo(orgSlug: string, teamId: string): Promise<TeamContactInfo> {
  return apiFetch<TeamContactInfo>(`/organizations/by-slug/${orgSlug}/teams/${teamId}/primary-contact`)
}

/** Changes who the league reaches about one team for one event. Team managers only. */
export async function setRegistrationContact(
  leagueOrgId: string, seasonId: string, registrationId: string, person: ContactChoice,
): Promise<void> {
  await apiFetch(`/seasons/${leagueOrgId}/${seasonId}/registrations/${registrationId}/contact`, {
    method: 'PUT',
    body: JSON.stringify({ person }),
  })
}

export function listMyCompetitions(): Promise<MyCompetition[]> {
  return apiFetch<MyCompetition[]>('/compete/mine')
}

/** One row of a season's standings table. */
export interface StandingRow {
  rank: number
  team_name: string
  team_slug: string
  team_logo_url: string
  org_name: string
  wins: number
  losses: number
  ties: number
  point_diff: number
}

export function getStandings(
  orgID: string,
  seasonID: string,
): Promise<{ standings: StandingRow[] }> {
  return apiFetch<{ standings: StandingRow[] }>(`/seasons/${orgID}/${seasonID}/standings`)
}
